import { getApiUrl } from '@/utils/apiConfig';
import { create } from 'zustand';

async function adminFetch(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('kaynin_auth_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const url = endpoint.startsWith('/api/') ? endpoint.replace('/api/', '/') : endpoint;
  const res = await fetch(`${getApiUrl()}${url}`, { ...options, headers });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
  }
  return res.json();
}

interface AdminStats {
  kpis: {
    citoyensInscrits: number;
    ong: number;
    partis: number;
    medias: number;
    institutions: number;
    candidatsDeclares: number;
    evenementsActifs: number;
    evenementsAttente: number;
    emissionsActives: number;
    emissionsAttente: number;
    interactions: number;
    engagements: number;
    alertes: number;
    alertesNonTraitees: number;
    publicitesActives: number;
    publicitesAttente: number;
    tauxValidationEvents: number;
  };
  charts: {
    userDistribution: any[];
    topParties: any[];
    activityOverTime: any[];
    engagementsByParty: any[];
    validationStats: any[];
    alertsByType: any[];
    topRegionsTable: any[];
    topPartiesTable: any[];
    regionRadar: any[];
  };
  demographics?: {
    citoyens: {
      total: number;
      hommes: number;
      femmes: number;
      age_18_24: number;
      age_25_34: number;
      age_35_44: number;
      age_45_54: number;
      age_55plus: number;
    };
    candidats: {
      total: number;
      hommes: number;
      femmes: number;
      age_18_24: number;
      age_25_34: number;
      age_35_44: number;
      age_45_54: number;
      age_55plus: number;
    };
  };
}

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  phone: string;
  partyAcronym: string | null;
  ongName: string | null;
  institutionName: string | null;
  channelName: string | null;
  qualite: string | null;
  profession: string | null;
  champsActivite: string | null;
  joinDate: string;
  accreditationStatus: string;
  region: string;
}

interface AdminDashboardStore {
  stats: AdminStats | null;
  users: AdminUser[];
  isLoadingStats: boolean;
  isLoadingUsers: boolean;
  error: string | null;
  fetchStats: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  deleteUser: (id: string) => Promise<boolean>;
  validateUser: (id: string, status: string) => Promise<boolean>;
}

export const useAdminDashboardStore = create<AdminDashboardStore>((set, get) => ({
  stats: null,
  users: [],
  isLoadingStats: false,
  isLoadingUsers: false,
  error: null,

  fetchStats: async () => {
    set({ isLoadingStats: true, error: null });
    try {
      const data = await adminFetch(`/api/admin_stats.php?_t=${Date.now()}`);
      set({ stats: data, isLoadingStats: false });
    } catch (err: any) {
      set({ error: err.message, isLoadingStats: false });
    }
  },

  fetchUsers: async () => {
    set({ isLoadingUsers: true, error: null });
    try {
      const data = await adminFetch('/api/admin_users.php');
      set({ users: data.users || [], isLoadingUsers: false });
    } catch (err: any) {
      set({ error: err.message, isLoadingUsers: false });
    }
  },

  deleteUser: async (id: string) => {
    try {
      await adminFetch(`/api/admin_users.php?id=${id}`, { method: 'DELETE' });
      set(state => ({ users: state.users.filter(u => u.id !== id) }));
      return true;
    } catch (err: any) {
      set({ error: err.message });
      return false;
    }
  },

  validateUser: async (id: string, status: string) => {
    try {
      await adminFetch('/api/admin_users.php', {
        method: 'POST',
        body: JSON.stringify({ id, status })
      });
      set(state => ({
        users: state.users.map(u => u.id === id ? { ...u, accreditationStatus: status } : u)
      }));
      return true;
    } catch (err: any) {
      set({ error: err.message });
      return false;
    }
  }
}));

